import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import ConfirmDialog from '../../components/ConfirmDialog';

const TYPE_META = {
  national: { label: 'National',  color: '#1e40af', bg: '#dbeafe' },
  state:    { label: 'State',     color: '#92400e', bg: '#fef3c7' },
  festival: { label: 'Festival',  color: '#5b21b6', bg: '#ede9fe' },
  company:  { label: 'Company',   color: '#065f46', bg: '#d1fae5' },
  optional: { label: 'Optional',  color: '#374151', bg: '#f3f4f6' },
};

const PRESET_COLORS = [
  '#1e40af','#d97706','#7c3aed','#0891b2','#16a34a',
  '#dc2626','#ca8a04','#ea580c','#be185d','#6b7280',
];

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
const DOW = ['S','M','T','W','T','F','S'];

const EMPTY_FORM = {
  holiday_name: '', holiday_date: '', holiday_type: 'festival',
  location: 'All', is_working_day: false, recurring: false,
  description: '', color: '#7c3aed',
};

function TypeBadge({ type }) {
  const m = TYPE_META[type] || TYPE_META.festival;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
      background: m.bg, color: m.color, whiteSpace: 'nowrap',
    }}>{m.label}</span>
  );
}

function MiniCalendar({ year, month, holidays }) {
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month;

  const holidayMap = {};
  holidays.forEach(h => {
    const d = new Date(h.holiday_date);
    if (d.getMonth() === month) {
      const day = d.getDate();
      if (!holidayMap[day]) holidayMap[day] = [];
      holidayMap[day].push(h);
    }
  });

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div style={{ background: 'var(--bg1)', borderRadius: 10, padding: '10px 8px', border: '1px solid var(--border)' }}>
      <div style={{ fontSize: 11, fontWeight: 700, textAlign: 'center', marginBottom: 6, color: 'var(--text1)' }}>
        {MONTH_NAMES[month]}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '1px 0' }}>
        {DOW.map((d, i) => (
          <div key={i} style={{ fontSize: 8, textAlign: 'center', color: 'var(--text3)', fontWeight: 600, paddingBottom: 3 }}>{d}</div>
        ))}
        {cells.map((day, i) => {
          if (!day) return <div key={i} />;
          const hs = holidayMap[day] || [];
          const primary = hs[0];
          const isToday = isCurrentMonth && day === today.getDate();
          let bg = 'transparent';
          let fg = 'var(--text2)';
          let border = 'none';
          if (primary) {
            if (primary.is_working_day) {
              fg = primary.color;
              border = `1.5px solid ${primary.color}`;
            } else {
              bg = primary.color;
              fg = '#fff';
            }
          }
          if (isToday && !primary) { bg = 'var(--accent-light)'; fg = 'var(--accent)'; }
          return (
            <div key={i} title={hs.map(h => h.holiday_name).join(', ')} style={{
              fontSize: 9, textAlign: 'center', padding: '2px 1px',
              borderRadius: 3, background: bg, color: fg, border,
              fontWeight: (primary || isToday) ? 700 : 400,
              position: 'relative',
            }}>
              {day}
              {hs.length > 1 && (
                <div style={{ position: 'absolute', top: 0, right: 1, width: 4, height: 4, borderRadius: '50%', background: hs[1].color }} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function HolidaysPage() {
  const qc = useQueryClient();
  const [year, setYear] = useState(new Date().getFullYear());
  const [view, setView] = useState('list');
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteHoliday, setDeleteHoliday] = useState(null);

  const { data: holidays = [], isLoading } = useQuery({
    queryKey: ['hr-holidays', year],
    queryFn: () => api.get('/hr/holidays', { params: { year } }).then(r => r.data),
  });

  const saveMutation = useMutation({
    mutationFn: data => editId
      ? api.patch(`/hr/holidays/${editId}`, data)
      : api.post('/hr/holidays', data),
    onSuccess: () => {
      qc.invalidateQueries(['hr-holidays']);
      toast.success(editId ? 'Holiday updated' : 'Holiday added');
      setShowModal(false);
    },
    onError: err => toast.error(err.response?.data?.error || 'Save failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: id => api.delete(`/hr/holidays/${id}`),
    onSuccess: () => {
      qc.invalidateQueries(['hr-holidays']);
      toast.success('Holiday removed');
      setDeleteHoliday(null);
    },
    onError: () => toast.error('Delete failed'),
  });

  function openAdd() {
    setEditId(null);
    setForm({ ...EMPTY_FORM, holiday_date: `${year}-01-01` });
    setShowModal(true);
  }

  function openEdit(h) {
    setEditId(h.id);
    setForm({
      holiday_name: h.holiday_name,
      holiday_date: h.holiday_date?.slice(0, 10) || '',
      holiday_type: h.holiday_type || 'festival',
      location: h.location || 'All',
      is_working_day: h.is_working_day || false,
      recurring: h.recurring || false,
      description: h.description || '',
      color: h.color || '#7c3aed',
    });
    setShowModal(true);
  }

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  const mandatory = holidays.filter(h => !h.is_working_day).length;
  const officeOpen = holidays.filter(h => h.is_working_day).length;

  // Group by month for list view
  const byMonth = {};
  holidays.forEach(h => {
    const m = new Date(h.holiday_date).getMonth();
    if (!byMonth[m]) byMonth[m] = [];
    byMonth[m].push(h);
  });

  if (isLoading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div>
      {/* Header */}
      <div className="section-head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h2>Holiday Calendar</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'var(--bg2)', borderRadius: 8, padding: '3px 6px', border: '1px solid var(--border)' }}>
            <button onClick={() => setYear(y => y - 1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text2)', fontWeight: 700, padding: '0 6px', fontSize: 16, lineHeight: 1 }}>‹</button>
            <span style={{ fontWeight: 700, fontSize: 15, minWidth: 44, textAlign: 'center' }}>{year}</span>
            <button onClick={() => setYear(y => y + 1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text2)', fontWeight: 700, padding: '0 6px', fontSize: 16, lineHeight: 1 }}>›</button>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ display: 'flex', borderRadius: 8, border: '1px solid var(--border)', overflow: 'hidden' }}>
            {[['list', '☰ List'], ['calendar', '⊞ Calendar']].map(([v, lbl]) => (
              <button key={v} onClick={() => setView(v)} style={{
                padding: '5px 12px', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer',
                background: view === v ? 'var(--accent)' : 'var(--bg1)',
                color: view === v ? '#fff' : 'var(--text2)',
              }}>{lbl}</button>
            ))}
          </div>
          <button className="btn-primary" onClick={openAdd}>+ Add Holiday</button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { label: 'Total in List', value: holidays.length, color: 'var(--accent)' },
          { label: 'Mandatory Off', value: mandatory, color: '#16a34a' },
          { label: 'Office Open', value: officeOpen, color: '#d97706', sub: 'observance, working day' },
        ].map(s => (
          <div key={s.label} style={{ background: 'var(--bg1)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 18px', minWidth: 130 }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 11, color: 'var(--text2)', fontWeight: 600 }}>{s.label}</div>
            {s.sub && <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 1 }}>{s.sub}</div>}
          </div>
        ))}
      </div>

      {/* ── LIST VIEW ── */}
      {view === 'list' && (
        <div>
          {Object.keys(byMonth).length === 0 && (
            <div className="card" style={{ textAlign: 'center', padding: 48, color: 'var(--text3)' }}>
              No holidays for {year}. Click "+ Add Holiday" to get started.
            </div>
          )}
          {Object.entries(byMonth).map(([m, hs]) => (
            <div key={m} style={{ marginBottom: 22 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 8, paddingLeft: 2 }}>
                {MONTH_NAMES[parseInt(m)]}
              </div>
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    {hs.map((h, idx) => {
                      const d = new Date(h.holiday_date);
                      const dayName = d.toLocaleDateString('en-IN', { weekday: 'short' });
                      const dayNum = d.getDate();
                      return (
                        <tr key={h.id} style={{ borderBottom: idx < hs.length - 1 ? '1px solid var(--border)' : 'none' }}>
                          <td style={{ width: 4, padding: 0, background: h.color || '#6366f1' }} />
                          <td style={{ width: 72, padding: '12px 16px', textAlign: 'center', borderRight: '1px solid var(--border)' }}>
                            <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1, color: h.is_working_day ? 'var(--text3)' : 'var(--text1)' }}>{dayNum}</div>
                            <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 600, marginTop: 2 }}>{dayName}</div>
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              <span style={{ fontWeight: 700, fontSize: 14 }}>{h.holiday_name}</span>
                              <TypeBadge type={h.holiday_type} />
                              {h.is_working_day && (
                                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: '#fef9c3', color: '#713f12', border: '1px dashed #d97706' }}>
                                  🏢 Office Open
                                </span>
                              )}
                              {h.recurring && <span style={{ fontSize: 10, color: 'var(--text3)' }}>↺ yearly</span>}
                            </div>
                            {h.description && (
                              <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>{h.description}</div>
                            )}
                            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{h.location}</div>
                          </td>
                          <td style={{ padding: '12px 16px', whiteSpace: 'nowrap', textAlign: 'right' }}>
                            <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                              <button className="btn btn-sm" onClick={() => openEdit(h)}>✎ Edit</button>
                              <button className="btn-danger btn-sm" onClick={() => setDeleteHoliday(h)}>✕</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── CALENDAR VIEW ── */}
      {view === 'calendar' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
            {Array.from({ length: 12 }, (_, m) => (
              <MiniCalendar key={m} year={year} month={m} holidays={holidays} />
            ))}
          </div>
          <div style={{ marginTop: 20, display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: 'var(--text2)', alignItems: 'center' }}>
            <span style={{ fontWeight: 700, color: 'var(--text3)', fontSize: 11 }}>LEGEND</span>
            {[
              { color: '#1e40af', label: 'National' },
              { color: '#d97706', label: 'State' },
              { color: '#7c3aed', label: 'Festival (Holiday)' },
              { color: '#16a34a', label: 'Company' },
            ].map(l => (
              <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{ width: 12, height: 12, borderRadius: 3, background: l.color }} />
                {l.label}
              </div>
            ))}
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <div style={{ width: 12, height: 12, borderRadius: 3, border: '1.5px solid #7c3aed' }} />
              Festival (Office Open)
            </div>
          </div>
        </div>
      )}

      {/* Add / Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 500 }}>
            <div className="modal-title">{editId ? 'Edit Holiday' : 'Add Holiday'}</div>
            <form onSubmit={e => { e.preventDefault(); saveMutation.mutate(form); }}>
              <div className="form-grid">
                <div className="field" style={{ gridColumn: '1/-1' }}>
                  <label>Holiday Name *</label>
                  <input value={form.holiday_name} onChange={e => set('holiday_name', e.target.value)}
                    placeholder="e.g. Gudi Padwa" required autoFocus />
                </div>
                <div className="field">
                  <label>Date *</label>
                  <input type="date" value={form.holiday_date} onChange={e => set('holiday_date', e.target.value)} required />
                </div>
                <div className="field">
                  <label>Type</label>
                  <select value={form.holiday_type} onChange={e => set('holiday_type', e.target.value)}>
                    <option value="national">National</option>
                    <option value="state">State</option>
                    <option value="festival">Festival</option>
                    <option value="company">Company</option>
                    <option value="optional">Optional</option>
                  </select>
                </div>
                <div className="field">
                  <label>Applicable to</label>
                  <input value={form.location} onChange={e => set('location', e.target.value)} placeholder="India, Maharashtra, All…" />
                </div>
                <div className="field">
                  <label>Color</label>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                    {PRESET_COLORS.map(c => (
                      <button key={c} type="button" onClick={() => set('color', c)} style={{
                        width: 24, height: 24, borderRadius: 6, background: c, border: 'none', cursor: 'pointer',
                        outline: form.color === c ? '2px solid var(--text1)' : '2px solid transparent',
                        outlineOffset: 2,
                      }} />
                    ))}
                    <input type="color" value={form.color} onChange={e => set('color', e.target.value)}
                      style={{ width: 24, height: 24, padding: 0, border: 'none', borderRadius: 6, cursor: 'pointer', background: 'none' }} />
                  </div>
                </div>
                <div className="field" style={{ gridColumn: '1/-1' }}>
                  <label>Description</label>
                  <input value={form.description} onChange={e => set('description', e.target.value)} placeholder="Optional note…" />
                </div>
              </div>

              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginTop: 14 }}>
                <input type="checkbox" checked={form.is_working_day} onChange={e => set('is_working_day', e.target.checked)} style={{ marginTop: 2, flexShrink: 0 }} />
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>Office is OPEN — employees must attend</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>Use this for observances like Gandhi Jayanti or Mahashivratri where you run the office</div>
                </div>
              </label>

              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginTop: 12 }}>
                <input type="checkbox" checked={form.recurring} onChange={e => set('recurring', e.target.checked)} style={{ marginTop: 2, flexShrink: 0 }} />
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>Recurring every year</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>Date must be updated manually each year — festival dates shift annually</div>
                </div>
              </label>

              {/* Preview */}
              <div style={{ marginTop: 16, padding: '10px 14px', borderRadius: 8, background: 'var(--bg2)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 5, alignSelf: 'stretch', borderRadius: 4, background: form.color, flexShrink: 0 }} />
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{form.holiday_name || 'Holiday Name'}</div>
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 3, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span>{form.holiday_date ? new Date(form.holiday_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : 'Date not set'}</span>
                    <TypeBadge type={form.holiday_type} />
                    {form.is_working_day && (
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 10, background: '#fef9c3', color: '#713f12' }}>🏢 Office Open</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? 'Saving…' : editId ? 'Save Changes' : 'Add Holiday'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteHoliday}
        title={`Remove "${deleteHoliday?.holiday_name}"?`}
        message="This holiday will be removed from the calendar."
        confirmLabel="Remove"
        onConfirm={() => deleteMutation.mutate(deleteHoliday.id)}
        onCancel={() => setDeleteHoliday(null)}
      />
    </div>
  );
}
